import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Play, Pause, Volume2, ChevronDown, X, RotateCcw, RotateCw, Share2, 
  Languages, SlidersHorizontal, Maximize2, Sparkles, Check, Copy, VolumeX, AlertCircle,
  Type, Minus, Plus, Bold, Italic, AlignLeft, AlignCenter, AlignRight, AlignJustify
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SavedSyncAudiobook, SentenceItem, syncAudiobookStorage, loadSavedSyncOffset, saveSyncOffset } from '../services/syncAudiobookStorage';
import { translationService } from '../services/translationService';
import { injectGhostNodes, decryptWindowPayload } from '../utils/audiobookCrypto';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AppBackButton } from './AppBackButton';
import T from './T';
import i18nInstance from '../i18n';

interface SyncedAudiobookPlayerProps {
  audiobook: SavedSyncAudiobook;
  onClose: () => void;
}

const TRANSCRIBED_LABEL: Record<string, string> = {
  pt: "Transcrito por LibriTui AI (pode conter erros)",
  en: "Transcribed by LibriTui AI (can make mistakes)",
  es: "Transcripto por LibriTui AI (puede cometer errores)",
  fr: "Transcrit par LibriTui AI (peut contenir des erreurs)",
  de: "Transkribiert von LibriTui AI (kann Fehler enthalten)",
  it: "Trascritto da LibriTui AI (può commettere errori)",
  hu: "A LibriTui AI által átírva (tartalmazhat hibákat)",
  ja: "LibriTui AI によって文字起こしされました（誤りが含まれる場合があります）",
  zh: "由 LibriTui AI 转录（可能会有错误）",
  ru: "Расшифровано LibriTui AI (может содержать ошибки)"
};

const NOW_PLAYING_LABEL: Record<string, string> = {
  pt: "REPRODUZINDO AGORA",
  en: "NOW PLAYING",
  es: "REPRODUCIENDO AHORA",
  fr: "EN LECTURE",
  de: "JETZT LÄUFT",
  it: "IN RIPRODUZIONE",
  hu: "MOSTANÁBAN JÁTSZOTT",
  ja: "再生中",
  zh: "正在播放",
  ru: "СEЙЧАС ИГРАЕТ"
};

export const SyncedAudiobookPlayer: React.FC<SyncedAudiobookPlayerProps> = ({ audiobook, onClose }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { targetLanguage, isSmartTranslationEnabled } = useTheme();

  // Check if player is on beta sync route (/settings/beta/sync-audiobooks)
  const isBetaSyncRoute = typeof window !== 'undefined' && (
    window.location.pathname.includes('/settings/beta/sync-audiobooks') ||
    window.location.pathname.includes('sync-audiobooks') ||
    window.location.href.includes('sync-audiobooks')
  );

  // Mode: Always maximized player (Image 2 & 3 style, no minimize option)
  const [viewMode, setViewMode] = useState<'maximized'>('maximized');

  // Format clean book title (e.g. "romeu-e-julieta-portuguese" -> "Romeu E Julieta")
  const displayTitle = useMemo(() => {
    let raw = audiobook.title || audiobook.id || '';
    raw = raw.replace(/\.(json|mp3)$/i, '');
    raw = raw.replace(/[-_](english|portuguese|spanish|french|german|italian|dutch|eng|por|spa|fra|deu|ita|nld)$/i, '');
    raw = raw.replace(/^\d+[-_]?/, '');
    if (raw.includes('-') || raw.includes('_')) {
      raw = raw.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    }
    return raw.trim() || audiobook.title;
  }, [audiobook.title, audiobook.id]);

  // Player Reading & Text Customization Settings
  const [playerFontSize, setPlayerFontSize] = useState(18); // in px
  const [playerIsBold, setPlayerIsBold] = useState(false);
  const [playerIsItalic, setPlayerIsItalic] = useState(false);
  const [playerTextAlign, setPlayerTextAlign] = useState<'left' | 'center' | 'right' | 'justify'>('center');
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // v3: vários MP3 numa linha do tempo global. Todo "tempo" do player (currentTime, savedTime,
  // sincronia) é global; o tempo local do <audio> = global - início da faixa atual.
  const tracks = audiobook.tracks && audiobook.tracks.length > 0 ? audiobook.tracks : null;
  const tracksTotal = tracks ? tracks.reduce((a, t) => a + t.duration, 0) : 0;
  const trackIndexAt = (t: number) => {
    if (!tracks) return 0;
    let k = tracks.length - 1;
    while (k > 0 && tracks[k].start > t) k--;
    return k;
  };
  const [trackIdx, setTrackIdx] = useState(() => trackIndexAt(audiobook.savedTime || 0));
  const trackStart = tracks ? tracks[trackIdx].start : 0;
  const trackStartRef = useRef(trackStart);
  trackStartRef.current = trackStart;
  const pendingSeekRef = useRef<{ local: number; play: boolean } | null>(null);

  // Audio & Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(audiobook.savedTime || 0);
  const [smoothTime, setSmoothTime] = useState(audiobook.savedTime || 0);
  const rafIdRef = useRef<number | null>(null);

  // 60fps / 120fps continuous animation frame loop for fluid Tale.fyi progressive highlight
  useEffect(() => {
    if (!isPlaying) {
      setSmoothTime(currentTime);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      return;
    }

    const updateSmoothTime = () => {
      if (audioRef.current) {
        setSmoothTime(trackStartRef.current + audioRef.current.currentTime);
      }
      rafIdRef.current = requestAnimationFrame(updateSmoothTime);
    };

    rafIdRef.current = requestAnimationFrame(updateSmoothTime);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [isPlaying, currentTime]);
  // Com várias faixas, a duração exibida é a do livro inteiro (soma das faixas)
  const [duration, setDuration] = useState(tracksTotal || audiobook.total_duration_seconds || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Speed state: 1x -> 1.5x -> 2x -> 0.75x
  const speedCycle = [1, 1.5, 2, 0.75];
  const [speedIndex, setSpeedIndex] = useState(0);
  const currentSpeed = speedCycle[speedIndex];

  // Sync Offset state (in seconds). Fica salvo por livro no navegador.
  const offsetBookKey = audiobook.book_id || audiobook.id.replace(/^r2_/, '');
  const [syncOffset, setSyncOffset] = useState(() => audiobook.syncOffset || loadSavedSyncOffset(offsetBookKey));
  const [showSyncAdjustModal, setShowSyncAdjustModal] = useState(false);

  // Share Modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  // Translation Mode state
  const [isTranslationActive, setIsTranslationActive] = useState(false);
  const [selectedWord, setSelectedWord] = useState<{
    word: string;
    translation: string;
    loading: boolean;
  } | null>(null);

  // Session Token State for Anti-Scraping Protection
  const [sessionToken, setSessionToken] = useState<string | null>(audiobook.sessionToken || null);
  const sessionTokenRef = useRef<string | null>(sessionToken);
  sessionTokenRef.current = sessionToken;

  // Initialize session token if missing
  useEffect(() => {
    if (!sessionToken && audiobook.book_id) {
      const bId = audiobook.book_id || audiobook.id.replace(/^r2_/, '');
      fetch('/api/player/session/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookId: bId })
      })
      .then(res => res.json())
      .then(data => {
        if (data.token) setSessionToken(data.token);
      })
      .catch(() => {});
    }
  }, [audiobook.book_id, audiobook.id, sessionToken]);

  // Heartbeat timer (every 75 seconds while playing)
  useEffect(() => {
    let timer: any = null;
    if (isPlaying && sessionToken) {
      timer = setInterval(() => {
        fetch('/api/player/session/heartbeat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: sessionToken, currentTime })
        })
        .then(res => res.json())
        .then(data => {
          if (data.token) setSessionToken(data.token);
        })
        .catch(() => {});
      }, 75000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isPlaying, sessionToken, currentTime]);

  // Debounced Progress Saving to D1 favorited_audiobooks table
  const lastProgressSavedTimeRef = useRef<number>(0);
  useEffect(() => {
    if (isPlaying && currentTime > 0 && Math.abs(currentTime - lastProgressSavedTimeRef.current) >= 5) {
      lastProgressSavedTimeRef.current = currentTime;
      const bId = audiobook.book_id || audiobook.id.replace(/^r2_/, '');
      const sId = audiobook.slug || bId;
      fetch('/api/audiobooks/progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || ''
        },
        body: JSON.stringify({
          audiobook_id: bId,
          audiobook_slug: sId,
          current_time: currentTime,
          current_paragraph_id: activeSentence?.id || null,
          duration: duration || audiobook.total_duration_seconds || 0
        })
      }).catch(() => {});
    }
  }, [currentTime, isPlaying, audiobook, duration, user?.id]);

  // Study Plan Goal Tracker for Audiobooks (Exibe aviso ao concluir a meta sem pausar o áudio)
  const [studyPlanGoalReached, setStudyPlanGoalReached] = useState(false);
  const sessionListeningSecondsRef = useRef<number>(0);
  const lastTickTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isPlaying) {
      lastTickTimeRef.current = Date.now();
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const deltaSec = (now - lastTickTimeRef.current) / 1000;
      lastTickTimeRef.current = now;

      sessionListeningSecondsRef.current += deltaSec;

      try {
        const rawTarget = localStorage.getItem('libritui_audiobook_target_minutes');
        if (rawTarget) {
          const targetMin = parseFloat(rawTarget);
          if (targetMin > 0 && sessionListeningSecondsRef.current >= targetMin * 60 && !studyPlanGoalReached) {
            setStudyPlanGoalReached(true);
            localStorage.removeItem('libritui_audiobook_target_minutes');
            // Notificar usuário sem pausar o áudio
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('study_plan_task_completed', {
                detail: { type: 'synced_audiobook', minutes: targetMin }
              }));
            }
          }
        }
      } catch (e) {}
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, studyPlanGoalReached]);

  // Audio element reference & Audio Blob URL
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const initialTimeSetRef = useRef(false);

  // Create Object URL for audio blob or use direct audioSrc on mount
  useEffect(() => {
    if (audiobook.audioBlob) {
      const url = URL.createObjectURL(audiobook.audioBlob);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (tracks) {
      setAudioUrl(tracks[trackIdx].url);
    } else if (audiobook.audioSrc) {
      setAudioUrl(audiobook.audioSrc);
    }
  }, [audiobook.id, audiobook.audioSrc, audiobook.audioBlob, trackIdx]);

  // Handle Audio load & saved timestamp (e troca de faixa: aplica a posição pendente quando a faixa carrega)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (!initialTimeSetRef.current) {
      pendingSeekRef.current = pendingSeekRef.current || { local: (audiobook.savedTime || 0) - trackStartRef.current, play: false };
      initialTimeSetRef.current = true;
    }
    // Trocar o src zera a velocidade: defaultPlaybackRate mantém a escolhida
    audio.defaultPlaybackRate = currentSpeed;
    audio.playbackRate = currentSpeed;
    audio.volume = isMuted ? 0 : volume;

    const applyPendingSeek = () => {
      const p = pendingSeekRef.current;
      if (!p) return;
      pendingSeekRef.current = null;
      audio.currentTime = Math.max(0, p.local);
      if (p.play) audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    };

    const handleLoadedMetadata = () => {
      if (!tracks && audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
      applyPendingSeek();
    };

    if (audio.readyState >= 1) applyPendingSeek();
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audioUrl]);

  // Sync Audio playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = currentSpeed;
    }
  }, [currentSpeed]);

  // Sync Volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Time update listener & persistence
  const lastSavedTimeRef = useRef(0);
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (pendingSeekRef.current) return; // faixa trocando: ainda não está na posição certa
    const cur = trackStartRef.current + audio.currentTime;
    setCurrentTime(cur);

    if (Math.abs(cur - lastSavedTimeRef.current) > 3) {
      lastSavedTimeRef.current = cur;
      syncAudiobookStorage.updateProgress(audiobook.id, cur);
    }
  };

  const globalNow = () => trackStartRef.current + (audioRef.current?.currentTime || 0);

  /** Vai para um tempo global; troca de MP3 quando o tempo cai em outra faixa. */
  const seekGlobal = (target: number, play?: boolean) => {
    const audio = audioRef.current;
    if (!audio) return;
    const maxT = duration || audio.duration || 99999;
    const t = Math.max(0, Math.min(maxT - 0.25, target));
    setCurrentTime(t);
    setSmoothTime(t);
    const shouldPlay = play ?? !audio.paused;
    const k = trackIndexAt(t);
    if (tracks && k !== trackIdx) {
      pendingSeekRef.current = { local: t - tracks[k].start, play: shouldPlay };
      setTrackIdx(k);
      return;
    }
    audio.currentTime = t - trackStartRef.current;
    if (shouldPlay && audio.paused) audio.play().then(() => setIsPlaying(true)).catch(() => {});
  };

  const handleAudioEnded = () => {
    // Fim de uma faixa do meio do livro: segue direto para a próxima
    if (tracks && trackIdx < tracks.length - 1) {
      seekGlobal(tracks[trackIdx + 1].start, true);
      return;
    }
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      syncAudiobookStorage.updateProgress(audiobook.id, globalNow());
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const skipSeconds = (secs: number) => {
    if (!audioRef.current) return;
    seekGlobal(globalNow() + secs);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    seekGlobal(parseFloat(e.target.value));
  };

  const cycleSpeed = () => {
    const nextIdx = (speedIndex + 1) % speedCycle.length;
    setSpeedIndex(nextIdx);
  };

  // Sync Offset adjustment handlers
  const handleAdjustOffset = (delta: number) => {
    const newOffset = Math.round((syncOffset + delta) * 10) / 10;
    setSyncOffset(newOffset);
    saveSyncOffset(offsetBookKey, newOffset);
    syncAudiobookStorage.updateOffset(audiobook.id, newOffset);
  };

  const handleResetOffset = () => {
    setSyncOffset(0);
    saveSyncOffset(offsetBookKey, 0);
    syncAudiobookStorage.updateOffset(audiobook.id, 0);
  };

  // Dynamic sentence loading & Chunk prefetching with Deduplicated Memory Cache
  const [loadedSentences, setLoadedSentences] = useState<SentenceItem[]>(() => {
    const raw = audiobook.sentences || [];
    return isBetaSyncRoute ? raw.slice(0, 20) : raw;
  });
  const fetchingChunkRef = useRef(false);
  const chunkCacheRef = useRef<Map<number, SentenceItem[]>>(new Map());
  const pendingOffsetsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const raw = audiobook.sentences || [];
    setLoadedSentences(isBetaSyncRoute ? raw.slice(0, 20) : raw);
    chunkCacheRef.current.clear();
    pendingOffsetsRef.current.clear();
  }, [audiobook.id, audiobook.sentences, isBetaSyncRoute]);

  const fetchChunkSafely = async (offsetSec: number) => {
    const isR2 = audiobook.id.startsWith('r2_') || !!audiobook.book_id;
    const chunkKey = Math.floor(offsetSec / 60);

    if (chunkCacheRef.current.has(chunkKey)) {
      const cached = chunkCacheRef.current.get(chunkKey)!;
      setLoadedSentences(prev => {
        const existingIds = new Set(prev.map(s => `${s.start}_${s.end}`));
        const newOnes = cached.filter(s => !existingIds.has(`${s.start}_${s.end}`));
        if (newOnes.length === 0) return prev;
        return [...prev, ...newOnes].sort((a, b) => a.start - b.start);
      });
      return;
    }

    if (pendingOffsetsRef.current.has(chunkKey) || fetchingChunkRef.current) return;
    pendingOffsetsRef.current.add(chunkKey);
    fetchingChunkRef.current = true;

    // 1. If local book stored in IndexedDB (not from R2 catalog)
    if (!isR2 || audiobook.id.startsWith('sync_')) {
      try {
        const localBook = await syncAudiobookStorage.getAudiobook(audiobook.id);
        if (localBook && Array.isArray(localBook.sentences)) {
          const windowStart = Math.max(0, offsetSec - 15);
          const windowEnd = offsetSec + 240;
          const chunk = localBook.sentences
            .filter(s => s.end >= windowStart && s.start <= windowEnd)
            .slice(0, 25);
          chunkCacheRef.current.set(chunkKey, chunk);
          setLoadedSentences(prev => {
            const existingIds = new Set(prev.map(s => `${s.start}_${s.end}`));
            const newOnes = chunk.filter(s => !existingIds.has(`${s.start}_${s.end}`));
            if (newOnes.length === 0) return prev;
            return [...prev, ...newOnes].sort((a, b) => a.start - b.start);
          });
        }
      } catch (err) {
        console.warn('[SyncedPlayer] Local chunk error:', err);
      } finally {
        fetchingChunkRef.current = false;
      }
      return;
    }

    // 2. If R2 Cloudflare book
    const bookId = audiobook.book_id || audiobook.id.replace(/^r2_/, '');
    const chunkUrl = `/api/sync-audiobooks/${encodeURIComponent(bookId)}/chunk?offset=${offsetSec}${audiobook.folder ? `&folder=${encodeURIComponent(audiobook.folder)}` : ''}`;
    // v3: a janela exige o token da sessão e vem cifrada com ele. Token vencido (pausa longa) -> renova e tenta de novo.
    const fetchChunk = async () => {
      const get = (tk: string | null) => fetch(chunkUrl, { headers: tk ? { 'x-player-token': tk } : {} });
      let tk = sessionTokenRef.current;
      let res = await get(tk);
      if (res.status === 401) {
        const init = await fetch('/api/player/session/init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookId })
        }).then(r => r.json()).catch(() => ({}));
        if (init.token) {
          tk = init.token;
          sessionTokenRef.current = init.token;
          setSessionToken(init.token);
          res = await get(tk);
        }
      }
      const data = await res.json();
      if (data?.encrypted && tk) {
        data.sentences = await decryptWindowPayload(data.encrypted.ciphertext, data.encrypted.iv, tk);
      }
      return data;
    };
    fetchChunk()
      .then(data => {
        if (data && Array.isArray(data.sentences) && data.sentences.length > 0) {
          chunkCacheRef.current.set(chunkKey, data.sentences);
          setLoadedSentences(prev => {
            const existingIds = new Set(prev.map(s => `${s.start}_${s.end}`));
            const newOnes = data.sentences.filter((s: SentenceItem) => !existingIds.has(`${s.start}_${s.end}`));
            if (newOnes.length === 0) return prev;
            return [...prev, ...newOnes].sort((a, b) => a.start - b.start);
          });
        }
      })
      .catch(err => console.warn('[SyncedPlayer] Chunk prefetch error:', err))
      .finally(() => {
        fetchingChunkRef.current = false;
      });
  };

  useEffect(() => {
    const minLoadedTime = loadedSentences.length > 0 ? loadedSentences[0].start : 0;
    const maxLoadedTime = loadedSentences.reduce((max, s) => Math.max(max, s.end), 0);
    const totalDuration = duration || audiobook.total_duration_seconds || 99999;

    // Prefetch chunk when approaching the end of loaded sentences (within 45s) or if currentTime is outside range
    const isApproachingEnd = maxLoadedTime > 0 && currentTime >= maxLoadedTime - 45 && currentTime < totalDuration;
    const isOutsideRange = (currentTime < minLoadedTime && currentTime > 0) || (currentTime > maxLoadedTime && maxLoadedTime > 0);

    if (isApproachingEnd || isOutsideRange) {
      fetchChunkSafely(Math.floor(currentTime));
    }
  }, [currentTime, loadedSentences, audiobook.id, duration]);

  // High-precision effective play time
  const effectivePlayTime = ((isBetaSyncRoute || tracks) && isPlaying) ? smoothTime : currentTime;
  const sentences = loadedSentences.length > 0 ? loadedSentences : (audiobook.sentences || []);

  // Binary Search to locate active sentence index matching (effectivePlayTime + syncOffset)
  const activeSentenceIndex = useMemo(() => {
    if (!sentences || sentences.length === 0) return 0;
    const adjustedTime = effectivePlayTime + syncOffset;
    let low = 0;
    let high = sentences.length - 1;
    let result = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const s = sentences[mid];
      if (adjustedTime >= s.start && adjustedTime < s.end) {
        return mid;
      } else if (adjustedTime < s.start) {
        high = mid - 1;
      } else {
        result = mid;
        low = mid + 1;
      }
    }
    return result;
  }, [sentences, effectivePlayTime, syncOffset]);

  const activeSentence = sentences[activeSentenceIndex] || null;

  // Fluid interpolation progress in active block (0 to 1) for Tale.fyi style
  const activeBlockProgress = useMemo(() => {
    if (!activeSentence) return 0;
    const sStart = activeSentence.start - syncOffset;
    const sEnd = activeSentence.end - syncOffset;
    const sDur = Math.max(0.1, sEnd - sStart);
    const elapsed = Math.max(0, effectivePlayTime - sStart);
    return Math.min(1, Math.max(0, elapsed / sDur));
  }, [activeSentence, effectivePlayTime, syncOffset]);

  // v3: frase sendo falada dentro do bloco ativo (+ progresso 0..1 dentro dela)
  const activePhrase = useMemo(() => {
    const list = activeSentence?.phrases;
    if (!list || list.length === 0) return null;
    const t = effectivePlayTime + syncOffset;
    let cur = list[0];
    for (const p of list) {
      if (p.s <= t) cur = p;
      else break;
    }
    const progress = t <= cur.s ? 0 : Math.min(1, (t - cur.s) / Math.max(0.1, cur.e - cur.s));
    return { n: cur.n, s: cur.s, progress };
  }, [activeSentence, effectivePlayTime, syncOffset]);

  // Sentence Click -> Jump audio to sentence start (ou ao início da frase clicada, no v3)
  const handleSentenceClick = (sentence: SentenceItem, phraseStart?: number) => {
    if (!audioRef.current) return;
    seekGlobal(Math.max(0, (phraseStart ?? sentence.start) - syncOffset), true);
  };

  // 1-second Fade Out helper
  const fadeOutAndPause = (): Promise<void> => {
    return new Promise((resolve) => {
      const audio = audioRef.current;
      if (!audio || !isPlaying) {
        if (audio) audio.pause();
        setIsPlaying(false);
        resolve();
        return;
      }

      const startVol = audio.volume;
      const steps = 20;
      const stepMs = 50;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const factor = Math.max(0, 1 - currentStep / steps);
        audio.volume = startVol * factor;

        if (currentStep >= steps) {
          clearInterval(timer);
          audio.pause();
          audio.volume = startVol;
          setIsPlaying(false);
          resolve();
        }
      }, stepMs);
    });
  };

  // 1-second Fade In helper
  const fadeInAndPlay = (): Promise<void> => {
    return new Promise((resolve) => {
      const audio = audioRef.current;
      if (!audio) {
        resolve();
        return;
      }

      const targetVol = isMuted ? 0 : volume;
      audio.volume = 0;
      audio.play().then(() => {
        setIsPlaying(true);
        const steps = 20;
        const stepMs = 50;
        let currentStep = 0;

        const timer = setInterval(() => {
          currentStep++;
          const factor = Math.min(1, currentStep / steps);
          audio.volume = targetVol * factor;

          if (currentStep >= steps) {
            clearInterval(timer);
            audio.volume = targetVol;
            resolve();
          }
        }, stepMs);
      }).catch(() => resolve());
    });
  };

  const unifiedDict = useUnifiedDictionary();

  // Word Click Translation Handler
  const handleWordClick = async (word: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isTranslationActive) return;

    const clean = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!clean) return;

    await fadeOutAndPause();

    const activeNativeLang = (i18n.language || targetLanguage || 'pt').split('-')[0];
    unifiedDict.handleWordClick(e, clean, audiobook.language || 'en', activeNativeLang);
  };

  // Clicking 'X' on Translation Modal: closes modal, audio REMAINS PAUSED!
  const handleCloseWordTranslationOnly = () => {
    setSelectedWord(null);
  };

  // Clicking 'CONTINUAR OUVINDO': closes modal AND resumes audio with 1s fade-in!
  const handleContinueListening = async () => {
    setSelectedWord(null);
    await fadeInAndPlay();
  };

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      syncAudiobookStorage.updateProgress(audiobook.id, globalNow());
    }
    setIsPlaying(false);
    onClose();
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs) || secs < 0) return '0:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (h > 0) {
      return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
    }
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const activeLangKey = (i18n.language || 'pt').split('-')[0];
  const transcribedFooterText = TRANSCRIBED_LABEL[activeLangKey] || TRANSCRIBED_LABEL.pt;
  const nowPlayingLabelText = NOW_PLAYING_LABEL[activeLangKey] || NOW_PLAYING_LABEL.pt;
  // Player 2.0: Tale.fyi fluid progressive highlighter with Image 3 formatting & Image 4 underline
  const renderTaleFluidSentence = (
    text: string,
    isActive: boolean,
    progress: number
  ) => {
    const lines = text.split('\n');
    const totalChars = Math.max(1, text.length);

    let charCounter = 0;
    return lines.map((line, lIdx) => {
      const trimmed = line.trim();
      const isCharacterHeading = trimmed.length > 0 && trimmed.length < 35 && trimmed.toUpperCase() === trimmed && /[A-Z]/.test(trimmed);

      const tokens = line.split(/(\s+)/);

      return (
        <span key={lIdx} className={`block leading-relaxed ${isCharacterHeading ? 'font-bold text-warn tracking-wider text-center my-2 text-base' : ''}`}>
          {tokens.map((token, tIdx) => {
            const tokenLen = token.length;
            const tokenStart = charCounter;
            const tokenEnd = charCounter + tokenLen;
            if (isActive) {
              charCounter += tokenLen;
            }

            const isSpace = /^\s+$/.test(token);
            if (isSpace) {
              return <span key={tIdx} style={{ whiteSpace: 'pre' }}>{token}</span>;
            }

            const cleanToken = token.replace(/[^a-zA-Z]/g, '');
            const isAllUppercaseWord = cleanToken.length >= 2 && cleanToken.toUpperCase() === cleanToken;

            // Inactive block (Image 3 formatting: words uppercase in amber, rest 60% opacity)
            if (!isActive) {
              return (
                <span
                  key={tIdx}
                  onClick={(e) => isTranslationActive ? handleWordClick(token, e) : null}
                  className={`inline-block px-0.5 rounded transition-colors ${
                    isCharacterHeading || isAllUppercaseWord ? 'text-warn font-bold' : ''
                  } ${
                    isTranslationActive 
                      ? 'hover:bg-warn/30 hover:text-warn cursor-pointer underline decoration-warn/50 decoration-dashed' 
                      : ''
                  }`}
                >
                  {token}
                </span>
              );
            }

            // Active block: smooth underline / bar rising at the feet of the words (Image 1 & 4)
            const startRatio = tokenStart / totalChars;
            const endRatio = tokenEnd / totalChars;

            let underlineGradient = 'none';

            if (progress >= endRatio) {
              // Word already spoken -> Full solid yellow underline (3px) at foot
              underlineGradient = 'linear-gradient(to right, #E8823F 0%, #E8823F 100%)';
            } else if (progress > startRatio && progress < endRatio) {
              // Current active word -> Progressive underline wipe at foot
              const wordPct = Math.max(0, Math.min(100, ((progress - startRatio) / (endRatio - startRatio)) * 100));
              underlineGradient = `linear-gradient(to right, #E8823F 0%, #E8823F ${wordPct}%, transparent ${wordPct}%)`;
            }

            const isAmber = isCharacterHeading || isAllUppercaseWord;

            const spanStyle: React.CSSProperties = {
              display: 'inline-block',
              paddingBottom: '3px',
              backgroundImage: underlineGradient,
              backgroundPosition: '0 100%',
              backgroundSize: '100% 3px',
              backgroundRepeat: 'no-repeat',
              color: isAmber ? '#B08D57' : '#ffffff',
              fontWeight: isAmber ? 'bold' : undefined,
              transition: 'background-image 0.05s linear'
            };

            return (
              <span
                key={tIdx}
                onClick={(e) => isTranslationActive ? handleWordClick(token, e) : null}
                style={spanStyle}
                className={`transition-all ${
                  isTranslationActive 
                    ? 'hover:bg-warn/40 hover:text-surface cursor-pointer' 
                    : ''
                }`}
              >
                {token}
              </span>
            );
          })}
        </span>
      );
    });
  };

  // v3: palavras de uma frase, com o mesmo visual do player (maiúsculas em amarelo, tradução por clique).
  // `progress` (0..1) só na frase falada: sublinhado amarelo avançando sob as palavras.
  const renderPhraseTokens = (text: string, progress: number | null) => {
    const tokens = text.split(/(\s+)/);
    const total = Math.max(1, text.length);
    let pos = 0;
    return tokens.map((token, tIdx) => {
      const start = pos;
      pos += token.length;
      if (/^\s+$/.test(token)) return <span key={tIdx} style={{ whiteSpace: 'pre-wrap' }}>{token}</span>;

      const letters = token.replace(/[^\p{L}]/gu, '');
      const isUpper = letters.length >= 2 && letters.toUpperCase() === letters && letters.toLowerCase() !== letters;

      let underline = 'none';
      if (progress !== null) {
        const a = start / total;
        const b = pos / total;
        if (progress >= b) underline = 'linear-gradient(to right, #E8823F 0%, #E8823F 100%)';
        else if (progress > a) {
          const pct = Math.max(0, Math.min(100, ((progress - a) / (b - a)) * 100));
          underline = `linear-gradient(to right, #E8823F 0%, #E8823F ${pct}%, transparent ${pct}%)`;
        }
      }

      return (
        <span
          key={tIdx}
          onClick={(e) => isTranslationActive ? handleWordClick(token, e) : null}
          style={progress !== null ? {
            paddingBottom: '3px',
            backgroundImage: underline,
            backgroundPosition: '0 100%',
            backgroundSize: '100% 3px',
            backgroundRepeat: 'no-repeat',
            transition: 'background-image 0.05s linear'
          } : undefined}
          className={`rounded ${isUpper ? 'text-warn font-bold' : ''} ${
            isTranslationActive ? 'hover:bg-warn/30 hover:text-warn cursor-pointer underline decoration-warn/50 decoration-dashed' : ''
          }`}
        >
          {token}
        </span>
      );
    });
  };

  // Palavras-isca invisíveis entre as frases: não aparecem nem são lidas por leitores de tela,
  // mas sujam o texto de quem copia o DOM (innerText/textContent) para raspar o livro.
  const GHOST_WORDS = ['zephy', 'kalon', 'brim', 'lumix', 'vort', 'pyro', 'quar', 'flit', 'zorn', 'vexo'];
  const ghostFor = (seed: string) => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return GHOST_WORDS[Math.abs(h) % GHOST_WORDS.length];
  };
  const ghostStyle: React.CSSProperties = {
    position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)', whiteSpace: 'nowrap', pointerEvents: 'none'
  };

  // v3: bloco (parágrafo) dividido em frases; no bloco ativo a frase falada fica destacada
  const renderPhrasedBlock = (s: SentenceItem, isActive: boolean) => {
    const texts = s.phraseTexts || [];
    const spokenN = isActive && activePhrase ? activePhrase.n : -1;
    const spokenStart = isActive && activePhrase ? activePhrase.s : -1;
    return texts.map((text, i) => {
      const timing = s.phrases?.find(p => p.n === i);
      const isSpoken = i === spokenN;
      const isPast = isActive && !isSpoken && timing !== undefined && timing.s < spokenStart;
      return (
        <React.Fragment key={i}>
          <span
            data-phrase={i}
            className={`rounded-md transition-colors duration-200 ${
              isSpoken ? 'bg-warn/20 text-white' : isActive ? (isPast ? 'text-white/90' : 'text-white/70') : ''
            }`}
            style={{ boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone', padding: isSpoken ? '1px 2px' : undefined }}
          >
            {renderPhraseTokens(text, isSpoken ? activePhrase!.progress : null)}
          </span>
          {i < texts.length - 1 && (
            <>
              {' '}
              <span aria-hidden="true" style={ghostStyle}>{` ${ghostFor(`${s.id}_${i}`)} `}</span>
            </>
          )}
        </React.Fragment>
      );
    });
  };

  const renderSentenceWords = (text: string) => {
    const tokens = text.split(/(\s+)/);
    return tokens.map((token, idx) => {
      if (/^\s+$/.test(token)) return <span key={idx}>{token}</span>;
      return (
        <span
          key={idx}
          onClick={(e) => isTranslationActive ? handleWordClick(token, e) : null}
          className={`inline-block transition-colors rounded px-0.5 ${
            isTranslationActive 
              ? 'hover:bg-warn/30 hover:text-warn cursor-pointer underline decoration-warn/50 decoration-dashed' 
              : ''
          }`}
        >
          {token}
        </span>
      );
    });
  };

  const renderFormattedSentenceText = (text: string, isInteractive: boolean = false) => {
    const lines = text.split('\n');
    if (lines.length === 1) {
      return isInteractive ? renderSentenceWords(text) : text;
    }
    return lines.map((line, idx) => {
      const trimmed = line.trim();
      const isCharacterName = trimmed.length > 0 && trimmed.length < 30 && trimmed.toUpperCase() === trimmed;
      return (
        <div
          key={idx}
          className={isCharacterName ? (isInteractive ? "font-bold text-warn mt-2 tracking-wide text-base" : "font-bold text-warn/70 mt-1") : ""}
        >
          {isInteractive ? renderSentenceWords(line) : line}
        </div>
      );
    });
  };

  const pastSentences = useMemo(() => [
    sentences[activeSentenceIndex - 3],
    sentences[activeSentenceIndex - 2],
    sentences[activeSentenceIndex - 1]
  ].filter(Boolean), [sentences, activeSentenceIndex]);

  const futureSentences = useMemo(() => [
    sentences[activeSentenceIndex + 1],
    sentences[activeSentenceIndex + 2],
    sentences[activeSentenceIndex + 3]
  ].filter(Boolean), [sentences, activeSentenceIndex]);

  // Scroll container ref & sentence element refs for Spotify Lyrics style smooth scrolling
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentenceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const prevActiveIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (viewMode !== 'maximized') return;

    // Only scroll if activeSentenceIndex actually changed
    if (prevActiveIndexRef.current === activeSentenceIndex) return;
    prevActiveIndexRef.current = activeSentenceIndex;

    const activeS = sentences[activeSentenceIndex];
    if (!activeS) return;

    const container = containerRef.current;
    const activeElement = sentenceRefs.current[activeS.id];

    if (container && activeElement) {
      const targetScroll = (activeElement.offsetTop + activeElement.offsetHeight / 2) - (container.clientHeight / 2);
      container.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }
  }, [activeSentenceIndex, sentences, viewMode]);

  useEffect(() => {
    if (viewMode === 'maximized') {
      prevActiveIndexRef.current = null;
      const activeS = sentences[activeSentenceIndex];
      if (!activeS) return;
      const timer = setTimeout(() => {
        const container = containerRef.current;
        const activeElement = sentenceRefs.current[activeS.id];
        if (container && activeElement) {
          const targetScroll = (activeElement.offsetTop + activeElement.offsetHeight / 2) - (container.clientHeight / 2);
          container.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [viewMode]);

  // Draggable constraint ref & opacity state for minimized mode
  const constraintsRef = useRef<HTMLDivElement | null>(null);
  const [isReleasedAfterDrag, setIsReleasedAfterDrag] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  if (!audioUrl) return null;

  return (
    <>
      {/* Single Persistent Audio Element (Never unmounts on viewMode change) */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleAudioEnded}
        className="hidden"
      />

      {/* --- Unified Maximized Transcribed Text Player (No minimize option) --- */}
      <div className="fixed inset-0 z-[99999] bg-slate-950/90 backdrop-blur-xl flex flex-col items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in duration-300">
        <div className="relative w-full max-w-lg bg-slate-900/95 border border-white/10 rounded-[1.75rem] sm:rounded-[28px] flex flex-col h-[calc(100dvh-16px)] max-h-[calc(100dvh-16px)] sm:h-[86vh] sm:max-h-[86vh] overflow-hidden my-auto">
          {/* Top Header Bar */}
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 shrink-0">
            {/* Voltar padrão do app + título do livro */}
            <div className="flex items-center gap-3 min-w-0 max-w-[55%]">
              <AppBackButton onClick={handleClosePlayer} className="shrink-0" />
              <h4 className="text-sm font-medium text-white truncate leading-tight tracking-tight" title={displayTitle}>
                {displayTitle}
              </h4>
            </div>

            {/* Top Right Action Buttons (APENAS ÍCONES: Tradutor, Configurações, Compartilhar, Fechar) */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Translation Toggle (Icon Only) */}
              <button
                onClick={() => setIsTranslationActive(!isTranslationActive)}
                className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl transition-all flex items-center justify-center ${
                  isTranslationActive 
                    ? 'bg-warn text-ink' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
                title="Tradutor"
              >
                <Languages size={18} />
              </button>

              {/* Settings (Icon Only) */}
              <button
                onClick={() => setShowSettingsModal(true)}
                className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl transition-all flex items-center justify-center relative ${
                  showSettingsModal || syncOffset !== 0
                    ? 'bg-warn text-ink' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
                title={i18nInstance.t('reader.settings')}
              >
                <SlidersHorizontal size={18} />
                {syncOffset !== 0 && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-warn border border-slate-900" />
                )}
              </button>

              {/* Share (Icon Only) */}
              <button
                onClick={() => setShowShareModal(true)}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                title="Compartilhar"
              >
                <Share2 size={18} />
              </button>

            </div>
          </div>

            {/* MAIN BODY: Karaoke Text Center Stage (Spotify Lyrics / Tale.fyi Style Smooth Scroll) */}
            <div 
              ref={containerRef}
              onCopy={(e) => e.preventDefault()}
              className="sync-reader-text relative flex-1 min-h-0 overflow-y-auto px-4 select-none [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-500/70 space-y-4"
              style={{ textAlign: playerTextAlign, userSelect: 'none', WebkitUserSelect: 'none' }}
            >
              {(() => {
                // Sliding window virtualization: renders only ~20 blocks in DOM for zero lag
                const WINDOW_PREV = 8;
                const WINDOW_NEXT = 12;
                const windowStart = Math.max(0, activeSentenceIndex - WINDOW_PREV);
                const windowEnd = Math.min(sentences.length, activeSentenceIndex + WINDOW_NEXT);
                const visibleSentences = sentences.slice(windowStart, windowEnd);

                return (
                  <>
                    {/* Espaço para a frase ativa ficar no meio. Antes era py-[32vh] no contêiner:
                        padding não encolhe em flex e empurrava o rodapé (play, velocidade...) para fora. */}
                    <div aria-hidden className="h-[28vh] shrink-0" />
                    {windowStart > 0 && (
                      <div style={{ height: `${windowStart * 45}px`, flexShrink: 0 }} />
                    )}
                    {visibleSentences.map((s, sliceIdx) => {
                      const idx = windowStart + sliceIdx;
                      const isActive = idx === activeSentenceIndex;

                      return (
                        <div
                          key={s.id}
                          id={s.id}
                          ref={(el) => { sentenceRefs.current[s.id] = el; }}
                          onClick={(e) => {
                            if (isTranslationActive) return;
                            // v3: clique numa frase começa a tocar exatamente dela
                            const phraseEl = (e.target as HTMLElement).closest('[data-phrase]');
                            const n = phraseEl ? Number(phraseEl.getAttribute('data-phrase')) : NaN;
                            const timing = Number.isFinite(n) ? s.phrases?.find(p => p.n === n) : undefined;
                            handleSentenceClick(s, timing?.s);
                          }}
                          style={{
                            willChange: 'opacity',
                            fontSize: `${playerFontSize}px`,
                            opacity: isActive ? 1 : 0.6,
                            fontWeight: playerIsBold ? 'bold' : (isActive ? '600' : 'normal'),
                            fontStyle: playerIsItalic ? 'italic' : undefined,
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            transition: 'opacity 0.3s ease'
                          }}
                          className={`reader-block max-w-xl mx-auto w-full shrink-0 ${
                            isTranslationActive ? 'cursor-default' : 'cursor-pointer'
                          } py-2.5 px-3 bg-transparent border-transparent shadow-none text-center select-none ${
                            isActive ? 'text-white is-active' : 'text-white/60 hover:opacity-80'
                          }`}
                        >
                          {s.heading && (
                            <span className="block font-bold text-warn tracking-wider text-center mb-3 text-base">
                              {s.heading}
                            </span>
                          )}
                          {s.phraseTexts && s.phraseTexts.length > 0 ? (
                            <span className="relative block leading-relaxed">{renderPhrasedBlock(s, isActive)}</span>
                          ) : renderTaleFluidSentence(
                            s.text || (s.html ? s.html.replace(/<[^>]*>/g, '') : ''),
                            isActive,
                            activeBlockProgress
                          )}
                        </div>
                      );
                    })}
                    {windowEnd < sentences.length && (
                      <div style={{ height: `${(sentences.length - windowEnd) * 45}px`, flexShrink: 0 }} />
                    )}
                    <div aria-hidden className="h-[28vh] shrink-0" />
                  </>
                );
              })()}
            </div>

            {/* Footer Mandatory AI Disclaimer */}
            <div className="text-center py-1 border-t border-white/5 shrink-0 px-4">
              <p className="text-[10px] font-medium text-white/40 tracking-wider">
                {transcribedFooterText}
              </p>
            </div>

            {/* Playback Controls Footer (Image 2 style - 100% Visible & Never Cut Off) */}
            <div className="space-y-3 px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] bg-slate-900/95 border-t border-white/10 shrink-0 z-20">
              {/* Seekbar */}
              <div className="space-y-1">
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-warn"
                />
                <div className="flex justify-between text-[11px] text-white/50 font-bold">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Controls row */}
              <div className="flex items-center justify-between pt-1 pb-3">
                <button onClick={() => skipSeconds(-10)} className="p-2.5 text-white/80 hover:text-white transition-transform active:scale-95" title="-10s">
                  <RotateCcw size={22} />
                </button>

                <button
                  onClick={togglePlayPause}
                  className="w-14 h-14 rounded-full bg-surface text-ink flex items-center justify-center hover:scale-105 active:scale-95 transition-all"
                >
                  {isPlaying ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
                </button>

                <button onClick={() => skipSeconds(10)} className="p-2.5 text-white/80 hover:text-white transition-transform active:scale-95" title="+10s">
                  <RotateCw size={22} />
                </button>

                <button
                  onClick={cycleSpeed}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-semibold text-warn tracking-wider"
                  title="Velocidade"
                >
                  {currentSpeed}x
                </button>

                <div className="relative">
                  <button onClick={() => setShowVolumeSlider(!showVolumeSlider)} className="p-2.5 text-white/80 hover:text-white">
                    {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                  {showVolumeSlider && (
                    <div className="absolute bottom-12 right-0 bg-slate-900 border border-white/10 p-3 rounded-2xl flex flex-col items-center gap-2 z-50">
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={isMuted ? 0 : volume}
                        onChange={(e) => { setVolume(parseFloat(e.target.value)); setIsMuted(false); }}
                        className="w-24 h-1 bg-white/20 rounded accent-warn cursor-pointer"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

  {/* WORD TRANSLATION POPOVER MODAL */}
      {selectedWord && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-warn/30 rounded-3xl p-6 max-w-xs w-full text-center space-y-4 animate-in zoom-in-95">
            <div className="flex justify-between items-center border-b border-white/10 pb-3">
              <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-warn flex items-center gap-1">
                <Languages size={14} /> TRADUÇÃO
              </span>
              {/* Clicking 'X': closes translation window, BUT audio stays PAUSED */}
              <button onClick={handleCloseWordTranslationOnly} className="p-1 text-white/50 hover:text-white"><X size={16} /></button>
            </div>

            <div className="space-y-2 py-2">
              <h4 className="text-xl font-semibold text-white capitalize">{selectedWord.word}</h4>
              {selectedWord.loading ? (
                <p className="text-xs text-warn font-bold animate-pulse"><T k="ui.player.fetchingTranslation" /></p>
              ) : (
                <p className="text-base font-bold text-warn bg-warn/10 py-2.5 px-4 rounded-xl border border-warn/20">
                  {selectedWord.translation}
                </p>
              )}
            </div>

            {/* Clicking 'CONTINUAR OUVINDO': closes modal AND resumes audio with 1s fade-in */}
            <button
              onClick={handleContinueListening}
              className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-normal text-xs uppercase font-mono tracking-[0.14em] transition-all"
            >
              CONTINUAR OUVINDO
            </button>
          </div>
        </div>
      )}

      {/* READING & SYNC SETTINGS MODAL (Image 3 style) */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-[100000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-[28px] p-6 sm:p-8 max-w-sm w-full space-y-6 text-white animate-in zoom-in-95 relative" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg text-white tracking-tight"><T k="reader.readingSettings" /></h3>
                <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok mt-0.5"><T k="ui.player.personalize" /></p>
              </div>
              <button onClick={() => setShowSettingsModal(false)} className="p-1.5 text-white/50 hover:text-white transition-colors"><X size={20} /></button>
            </div>

            <div className="space-y-6 max-h-[65vh] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
              {/* Font Size */}
              <div className="space-y-2">
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok flex items-center gap-1.5">
                  <Type size={14} /> TAMANHO DA FONTE
                </span>
                <div className="flex items-center justify-between bg-white/5 p-2 rounded-2xl border border-white/10">
                  <button
                    onClick={() => setPlayerFontSize(prev => Math.max(12, prev - 2))}
                    className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-xl font-bold active:scale-95 transition-all text-white"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="text-base font-semibold text-white">{playerFontSize}px</span>
                  <button
                    onClick={() => setPlayerFontSize(prev => Math.min(32, prev + 2))}
                    className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-xl font-bold active:scale-95 transition-all text-white"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              {/* Text Style: Bold / Italic */}
              <div className="space-y-2">
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok flex items-center gap-1.5">
                  <Sparkles size={14} /> ESTILO DO TEXTO
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPlayerIsBold(!playerIsBold)}
                    className={`h-14 flex items-center justify-center gap-2 rounded-2xl border transition-all text-xs font-semibold ${
                      playerIsBold
                        ? 'border-ok bg-ok/20 text-ok'
                        : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <Bold size={18} /> NEGRITO
                  </button>
                  <button
                    onClick={() => setPlayerIsItalic(!playerIsItalic)}
                    className={`h-14 flex items-center justify-center gap-2 rounded-2xl border transition-all text-xs font-semibold ${
                      playerIsItalic
                        ? 'border-ok bg-ok/20 text-ok'
                        : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <Italic size={18} /> ITÁLICO
                  </button>
                </div>
              </div>

              {/* Alignment */}
              <div className="space-y-2">
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok flex items-center gap-1.5">
                  <AlignLeft size={14} /> ALINHAMENTO
                </span>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'left', icon: AlignLeft },
                    { id: 'center', icon: AlignCenter },
                    { id: 'right', icon: AlignRight },
                    { id: 'justify', icon: AlignJustify },
                  ].map((align) => (
                    <button
                      key={align.id}
                      onClick={() => setPlayerTextAlign(align.id as any)}
                      className={`h-14 flex items-center justify-center rounded-2xl border transition-all ${
                        playerTextAlign === align.id
                          ? 'border-ok bg-ok/20 text-ok'
                          : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <align.icon size={20} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Sync Adjust section inside Settings */}
              <div className="space-y-3 pt-3 border-t border-white/10">
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-warn flex items-center gap-1.5">
                  <SlidersHorizontal size={14} /> SINCRONIA DE TEXTO E ÁUDIO
                </span>
                <p className="text-[11px] leading-snug text-white/50">
                  Se o destaque estiver atrás da voz, use Adiantar; se estiver na frente, Atrasar. Vale na hora e fica salvo para este livro.
                </p>
                <div className="text-xs font-medium text-white/80">
                  Offset Atual: <span className="text-warn font-bold">{syncOffset > 0 ? `+${syncOffset}s` : `${syncOffset}s`}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleAdjustOffset(-0.5)}
                    className="py-3 bg-tintd-lavender/30 hover:bg-tintd-lavender/50 border border-tintd-lavender/30 rounded-xl font-bold text-xs text-tintd-lavender"
                  >
                    🐢 Atrasar (-0.5s)
                  </button>
                  <button
                    onClick={() => handleAdjustOffset(0.5)}
                    className="py-3 bg-warn/30 hover:bg-warn/50 border border-warn/30 rounded-xl font-bold text-xs text-warn"
                  >
                    🐇 Adiantar (+0.5s)
                  </button>
                </div>
                {syncOffset !== 0 && (
                  <button onClick={handleResetOffset} className="text-xs text-white/50 hover:text-white underline pt-1 block mx-auto">
                    Resetar Offset (0s)
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowSettingsModal(false)}
              className="text-[13px] font-medium w-full h-12 bg-warn text-ink rounded-xl hover:bg-warn transition-all"
            >
              Concluído
            </button>
          </div>
        </div>
      )}

      {/* MANUAL SYNC ADJUSTMENT MODAL */}
      {showSyncAdjustModal && (
        <div className="fixed inset-0 z-[100000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full space-y-6 text-white text-center animate-in zoom-in-95">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-sm text-warn flex items-center gap-2">
                <SlidersHorizontal size={18} /> Sincronia de Texto e Áudio
              </h3>
              <button onClick={() => setShowSyncAdjustModal(false)} className="p-1 text-white/50 hover:text-white"><X size={18} /></button>
            </div>

            <div className="bg-white/5 p-4 rounded-2xl border border-white/10 text-left space-y-2 text-xs text-white/80 leading-relaxed">
              <p className="font-bold text-warn"><T k="ui.player.syncHowTitle" /></p>
              <p>Caso o texto esteja aparecendo adiantado ou atrasado em relação à locução, use os botões abaixo para ajustar em 0,5 segundos. O ajuste fica salvo para este livro.</p>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-semibold text-white">
                Offset Atual: <span className="text-warn">{syncOffset > 0 ? `+${syncOffset}s` : `${syncOffset}s`}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleAdjustOffset(-0.5)}
                  className="py-3 bg-tintd-lavender/30 hover:bg-tintd-lavender/50 border border-tintd-lavender/30 rounded-xl font-bold text-xs text-tintd-lavender"
                >
                  🐢 Atrasar texto (-0.5s)
                </button>
                <button
                  onClick={() => handleAdjustOffset(0.5)}
                  className="py-3 bg-warn/30 hover:bg-warn/50 border border-warn/30 rounded-xl font-bold text-xs text-warn"
                >
                  🐇 Adiantar texto (+0.5s)
                </button>
              </div>

              {syncOffset !== 0 && (
                <button onClick={handleResetOffset} className="text-xs text-white/50 hover:text-white underline pt-2">
                  Resetar para 0s
                </button>
              )}
            </div>

            <button
              onClick={() => setShowSyncAdjustModal(false)}
              className="w-full py-3 bg-warn text-ink rounded-xl font-normal text-xs uppercase font-mono tracking-[0.14em]"
            >
              Concluído
            </button>
          </div>
        </div>
      )}

      {/* SHARE MODAL */}
      {showShareModal && (
        <div className="fixed inset-0 z-[100000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full space-y-6 text-white text-center animate-in zoom-in-95">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-sm text-warn">Compartilhar Audiobook</h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 text-white/50 hover:text-white"><X size={18} /></button>
            </div>

            {/* Share Card preview */}
            <div className="p-6 bg-gradient-to-br from-tint-lavender via-slate-900 to-tint-butter rounded-2xl border border-white/10 space-y-4">
              <div className="w-20 h-20 bg-warn/30 rounded-2xl mx-auto flex items-center justify-center border border-warn/20 text-3xl">
                📚
              </div>
              <div>
                <h4 className="font-semibold text-base text-white tracking-tight">{audiobook.title}</h4>
                <p className="text-xs text-white/70 font-medium mt-0.5">{audiobook.author}</p>
              </div>
              <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[10px] text-white/40 font-mono tracking-[0.14em] uppercase">
                <span>LibriTui Sync</span>
                <span>Audiobook</span>
              </div>
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(`Estou ouvindo "${audiobook.title}" por ${audiobook.author} no LibriTui!`);
                setCopiedShare(true);
                setTimeout(() => setCopiedShare(false), 2000);
              }}
              className="text-[13px] font-medium w-full h-12 bg-warn text-ink rounded-xl flex items-center justify-center gap-2 hover:bg-warn transition-all"
            >
              {copiedShare ? <Check size={16} /> : <Copy size={16} />}
              {copiedShare ? 'Copiado!' : 'Copiar Texto de Compartilhamento'}
            </button>
          </div>
        </div>
      )}

      {/* STUDY PLAN GOAL REACHED TOAST (Sem pausar a reprodução) */}
      {studyPlanGoalReached && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100005] bg-ok/95 text-white px-6 py-4 rounded-3xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] backdrop-blur-md flex items-center gap-4 animate-in slide-in-from-top-4 border border-ok/30 max-w-md w-[90%]">
          <div className="p-2 bg-white/20 rounded-2xl">
            <Sparkles size={24} className="text-warn" />
          </div>
          <div className="flex-1 text-left">
            <h4 className="font-medium text-sm"><T k="ui.player.dailyGoal" /></h4>
            <p className="text-xs text-white/90 font-medium mt-0.5">Sua tarefa de hoje foi registrada com sucesso. Continue ouvindo o quanto quiser!</p>
          </div>
          <button 
            onClick={() => setStudyPlanGoalReached(false)}
            className="p-1.5 hover:bg-white/10 rounded-full text-white/80 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </>
  );
};
